import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Page, PageHeader } from '../components/Page';
import { PCard } from '../components/PCard';
import { StatusPill } from '../components/StatusPill';
import { Async } from '../components/Async';
import { useFetch } from '../lib/useFetch';
import { useAuth } from '../lib/auth';
import {
  getPayable,
  finalizePayable,
  markPayablePaid,
  voidPayable,
  payablePdfUrl,
  payableSupportingCsvUrl,
  ApiError,
} from '../lib/api';
import { PAYABLE_STATUS } from '../lib/types';
import { fmtCents, fmtDate } from '../lib/format';

export const PayableDetailPage = () => {
  const { id = '' } = useParams();
  const { can } = useAuth();
  const state = useFetch(() => getPayable(id), [id]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  async function act(fn: () => Promise<unknown>, ok: string) {
    setBusy(true);
    setMsg(null);
    try {
      await fn();
      setMsg(ok);
      state.reload();
    } catch (err) {
      setMsg(err instanceof ApiError ? err.message : 'Action failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Page>
      <Async state={state}>
        {({ payable: p, payee }) => {
          const s = PAYABLE_STATUS[p.status];
          const unpriced = (p.snapshot?.unpricedProducts as string[] | undefined) ?? [];
          const deferred = (p.snapshot?.deferredCount as number | undefined) ?? 0;
          const manage = can('invoices:manage');
          return (
            <>
              <PageHeader
                title={p.invoiceNumber ?? `Payable ${p.id.slice(-6)}`}
                sub={
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <StatusPill tone={s.tone}>{s.label}</StatusPill>
                    <span>{p.payeeName} · NCPDP {p.ncpdpid} · {p.period.label}</span>
                  </span>
                }
                actions={
                  <>
                    <a className="btn" href={payablePdfUrl(p.id)} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>PDF</a>
                    <a className="btn" href={payableSupportingCsvUrl(p.id)} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>Supporting CSV</a>
                    {manage && p.status === 'draft' && (
                      <button className="btn btn--primary" disabled={busy} onClick={() => act(() => finalizePayable(p.id), 'Finalized — approved for payment.')}>
                        Finalize
                      </button>
                    )}
                    {manage && p.status === 'open' && (
                      <button className="btn btn--primary" disabled={busy} onClick={() => act(() => markPayablePaid(p.id, {}), 'Marked paid.')}>
                        Mark paid
                      </button>
                    )}
                    {manage && (p.status === 'draft' || p.status === 'open') && (
                      <button className="btn" disabled={busy} onClick={() => act(() => voidPayable(p.id), 'Voided.')}>
                        Void
                      </button>
                    )}
                  </>
                }
              />

              {msg && (
                <div style={{ padding: '8px 12px', borderRadius: 6, background: 'rgba(59,130,246,.08)', color: 'var(--status-in-progress-ink,#1d4ed8)', fontSize: 13 }}>
                  {msg}
                </div>
              )}

              <div className="grid grid-3">
                <PCard style={{ padding: 16 }}>
                  <div className="muted" style={{ fontSize: 12 }}>Total</div>
                  <div className="tabular" style={{ fontSize: 24, fontWeight: 700 }}>{fmtCents(p.totalCents)}</div>
                </PCard>
                <PCard style={{ padding: 16 }}>
                  <div className="muted" style={{ fontSize: 12 }}>Amount due</div>
                  <div className="tabular" style={{ fontSize: 24, fontWeight: 700 }}>{fmtCents(p.amountDueCents)}</div>
                </PCard>
                <PCard style={{ padding: 16 }}>
                  <div className="muted" style={{ fontSize: 12 }}>Issued · Due</div>
                  <div style={{ fontSize: 15, fontWeight: 600 }}>
                    {p.issuedAt ? fmtDate(p.issuedAt) : '—'} · {p.dueAt ? fmtDate(p.dueAt) : '—'}
                  </div>
                </PCard>
              </div>

              <div>
                <div className="section-title">Line items</div>
                <PCard>
                  <table className="p-table">
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th style={{ textAlign: 'right' }}>Fills</th>
                        <th style={{ textAlign: 'right' }}>Unit cost</th>
                        <th style={{ textAlign: 'right' }}>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {p.lineItems.map((li, i) => (
                        <tr key={i}>
                          <td>{li.description}</td>
                          <td className="tabular" style={{ textAlign: 'right' }}>{li.quantity}</td>
                          <td className="tabular" style={{ textAlign: 'right' }}>{fmtCents(li.unitCostCents)}</td>
                          <td className="tabular" style={{ textAlign: 'right' }}>{fmtCents(li.amountCents)}</td>
                        </tr>
                      ))}
                      <tr>
                        <td colSpan={3} style={{ textAlign: 'right', fontWeight: 700 }}>Total</td>
                        <td className="tabular" style={{ textAlign: 'right', fontWeight: 700 }}>{fmtCents(p.totalCents)}</td>
                      </tr>
                    </tbody>
                  </table>
                </PCard>
              </div>

              {deferred > 0 && (
                <PCard style={{ padding: 16 }}>
                  <div style={{ fontSize: 13, color: 'var(--p-muted-fg)' }}>
                    <strong style={{ color: 'var(--status-attention-ink,#b45309)' }}>{deferred} fills weren't priced</strong> and
                    were left off this payable (no matching cost). Add costs under{' '}
                    <Link to="/pharmacy-costs">Pharmacy Costs</Link>, then re-run.
                    {unpriced.length > 0 && (
                      <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
                        {unpriced.map((u) => <li key={u} className="mono" style={{ fontSize: 12 }}>{u}</li>)}
                      </ul>
                    )}
                  </div>
                </PCard>
              )}

              {payee?.remit && (payee.remit.bankName || payee.remit.accountNumber || payee.remit.instructions) && (
                <PCard style={{ padding: 16 }}>
                  <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>REMIT TO</div>
                  <div style={{ fontSize: 13 }}>
                    {payee.remit.accountName || payee.legalName || payee.name}
                    {payee.remit.bankName && <div>Bank: {payee.remit.bankName}</div>}
                    {payee.remit.accountNumber && <div>Account: {payee.remit.accountNumber}</div>}
                    {payee.remit.routingNumber && <div>Routing: {payee.remit.routingNumber}</div>}
                    {payee.remit.instructions && <div>{payee.remit.instructions}</div>}
                  </div>
                </PCard>
              )}
            </>
          );
        }}
      </Async>
    </Page>
  );
};
